import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import { apiFetch } from "../../lib/api-client";

export const Route = createFileRoute("/_app/grammar")({
  component: GrammarPage,
});

type ProviderChoice = "auto" | "gemini" | "openai";
type Kind = "wrong" | "less-natural";

type TeachGrammarResponse = {
  id: string;
  correctText: string;
  wrongText: string;
  kind: Kind;
  imageUrl: string;
  provider: "gemini" | "openai";
};

const teachGrammar = (input: {
  correctText: string;
  wrongText: string;
  kind: Kind;
  childName?: string;
  provider: ProviderChoice;
}): Promise<TeachGrammarResponse> => {
  const body: Record<string, string> = {
    correctText: input.correctText,
    wrongText: input.wrongText,
    kind: input.kind,
  };
  if (input.childName) body.childName = input.childName;
  if (input.provider !== "auto") body.provider = input.provider;
  return apiFetch<TeachGrammarResponse>("/teach/grammar", {
    method: "POST",
    body: JSON.stringify(body),
  });
};

const providerOptions: Array<{ value: ProviderChoice; label: string }> = [
  { value: "auto", label: "自動" },
  { value: "gemini", label: "Gemini" },
  { value: "openai", label: "OpenAI" },
];

const kindOptions: Array<{
  value: Kind;
  label: string;
  description: string;
}> = [
  {
    value: "wrong",
    label: "❌ 間違い",
    description: "文法ルール違反 (絵を文字通りに描かせて滑稽にする)",
  },
  {
    value: "less-natural",
    label: "△ 自然じゃない",
    description: "文法的には可だが、より自然な言い方がある (視点ハイライト)",
  },
];

const CHILD_NAME_KEY = "lexivision.childName";

function GrammarPage() {
  const [kind, setKind] = useState<Kind>("wrong");
  const [correctText, setCorrectText] = useState("");
  const [wrongText, setWrongText] = useState("");
  const [childName, setChildName] = useState("");
  const [provider, setProvider] = useState<ProviderChoice>("auto");

  // 子供の名前を localStorage に永続化
  useEffect(() => {
    const saved = localStorage.getItem(CHILD_NAME_KEY);
    if (saved) setChildName(saved);
  }, []);

  useEffect(() => {
    if (childName) localStorage.setItem(CHILD_NAME_KEY, childName);
  }, [childName]);

  const mutation = useMutation({ mutationFn: teachGrammar });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!correctText.trim() || !wrongText.trim()) return;
    const trimmedChildName = kind === "less-natural" ? childName.trim() : "";
    mutation.mutate({
      correctText: correctText.trim(),
      wrongText: wrongText.trim(),
      kind,
      provider,
      ...(trimmedChildName ? { childName: trimmedChildName } : {}),
    });
  };

  const isLessNatural = kind === "less-natural";

  // ラベル表記の切替
  const okMark = isLessNatural ? "◯" : "⭕";
  const ngMark = isLessNatural ? "△" : "❌";
  const okLabel = isLessNatural ? "より自然" : "正しい文";
  const ngLabel = isLessNatural ? "あまり自然じゃない" : "誤った文";
  const okPlaceholder = isLessNatural
    ? "例: ママから もらった"
    : "例: ぱんを たべる";
  const ngPlaceholder = isLessNatural
    ? "例: ママが あげた"
    : "例: ぱんが たべる";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">文法</h1>
        <p className="text-sm text-neutral-600 mt-1">
          2つの文を入れると、絵で対比して見せます
        </p>
      </div>

      {/* kind トグル */}
      <div className="flex flex-col gap-2">
        <span className="text-xs text-neutral-500">対比の種類:</span>
        <div className="grid grid-cols-2 gap-2">
          {kindOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setKind(opt.value)}
              className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                kind === opt.value
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white border-neutral-300 hover:border-neutral-500"
              }`}
            >
              <div className="text-sm font-semibold">{opt.label}</div>
              <div
                className={`text-[11px] mt-0.5 ${
                  kind === opt.value ? "text-neutral-300" : "text-neutral-500"
                }`}
              >
                {opt.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold text-emerald-700">
              {okMark} {okLabel}
            </span>
            <input
              type="text"
              value={correctText}
              onChange={(e) => setCorrectText(e.target.value)}
              placeholder={okPlaceholder}
              className="mt-1 w-full rounded-xl border border-emerald-300 bg-white px-4 py-3 text-lg outline-none focus:border-emerald-600"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-rose-700">
              {ngMark} {ngLabel}
            </span>
            <input
              type="text"
              value={wrongText}
              onChange={(e) => setWrongText(e.target.value)}
              placeholder={ngPlaceholder}
              className="mt-1 w-full rounded-xl border border-rose-300 bg-white px-4 py-3 text-lg outline-none focus:border-rose-600"
            />
          </label>
        </div>

        {isLessNatural && (
          <label className="block">
            <span className="text-xs text-neutral-500">
              子供の名前 (省略可、視点の主人公として絵に反映)
            </span>
            <input
              type="text"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder="例: ゆうり"
              className="mt-1 w-full rounded-xl border border-neutral-300 bg-white px-4 py-2 text-sm outline-none focus:border-neutral-600"
            />
          </label>
        )}

        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-neutral-500 self-center mr-1">
            画像生成:
          </span>
          {providerOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setProvider(opt.value)}
              className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                provider === opt.value
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "bg-white border-neutral-300 hover:border-neutral-500"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <button
          type="submit"
          disabled={mutation.isPending || !correctText || !wrongText}
          className="w-full rounded-xl bg-neutral-900 py-4 text-white text-lg font-semibold disabled:opacity-50 hover:bg-neutral-700 transition-colors"
        >
          {mutation.isPending ? "つくっています..." : "絵で見比べる"}
        </button>
      </form>

      {mutation.isPending && (
        <LoadingIndicator label="つくっています..." variant="bar" />
      )}

      {mutation.isError && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {mutation.error.message}
        </div>
      )}

      {mutation.data && (
        <ResultPanel data={mutation.data} okMark={okMark} ngMark={ngMark} />
      )}
    </div>
  );
}

function ResultPanel({
  data,
  okMark,
  ngMark,
}: {
  data: TeachGrammarResponse;
  okMark: string;
  ngMark: string;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-white">
        <img
          src={data.imageUrl}
          alt={`${data.correctText} / ${data.wrongText}`}
          className="w-full h-auto"
        />
      </div>
      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-3">
          <div className="text-xs text-rose-700 font-semibold">{ngMark}</div>
          <div className="text-lg font-bold mt-1">{data.wrongText}</div>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
          <div className="text-xs text-emerald-700 font-semibold">{okMark}</div>
          <div className="text-lg font-bold mt-1">{data.correctText}</div>
        </div>
      </div>
      <p className="text-xs text-neutral-500 text-right">{data.provider}</p>
    </div>
  );
}
